"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { observer } from "mobx-react-lite"
import { useAppContext } from "../../../shared/functions/Context"
import { defaultTrainingBudget, type ITrainingBudget } from "../../../shared/models/TrainingBudgetModal"

const Budget: React.FC = () => {
  const { store, api, ui } = useAppContext()
  const [budget, setBudget] = useState<ITrainingBudget>({ ...defaultTrainingBudget })
  const [loading, setLoading] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [typeError, setTypeError] = useState("")

  const allBudgets = store.trainingBudget.all

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true)
      try {
        const result = await api.trainingBudget.getAll()
        store.trainingBudget.setAll(result)
      } catch (error) {
        ui.snackbar.load({
          id: Date.now(),
          message: "Failed to load.",
          type: "danger",
        })
      }
      setLoading(false)
    }
    loadAll()
  }, [api.trainingBudget, ui.snackbar, store.trainingBudget])

  const handleSave = async () => {
    if (!budget.type) {
      setTypeError("Please select a budget type.")
      return
    }

    setLoading(true)
    try {
      const existingBudget = allBudgets.find((item) => item.asJson.type === budget.type)

      if (existingBudget) {
        const updatedBudget = {
          ...existingBudget.asJson,
          totalAmount: existingBudget.asJson.totalAmount + budget.totalAmount,
        }
        await api.trainingBudget.update(updatedBudget)
        ui.snackbar.load({
          id: Date.now(),
          message: `${budget.type} budget updated successfully.`,
          type: "success",
        })
      } else {
        await api.trainingBudget.create(budget)
        ui.snackbar.load({
          id: Date.now(),
          message: `${budget.type} budget created successfully.`,
          type: "success",
        })
      }
      setBudget({ ...defaultTrainingBudget })
      setIsEditing(false)
      setTypeError("")
    } catch (error) {
      ui.snackbar.load({
        id: Date.now(),
        message: "Failed to save.",
        type: "danger",
      })
    }
    setLoading(false)
  }

  const handleEdit = (id: string) => {
    const selectedBudget = allBudgets.find((item) => item.asJson.id === id)?.asJson
    if (selectedBudget) {
      setBudget(selectedBudget)
      setIsEditing(true)
      setTypeError("")
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount)
  }

  const totalBudget = allBudgets.reduce((sum, item) => sum + item.asJson.totalAmount, 0)

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h3 className="text-3xl font-bold text-gray-800 mb-2">Training Budget Management</h3>
        <div className="h-1 w-32 bg-[#84cbe9] rounded"></div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-gradient-to-br from-[#84cbe9] to-[#3f6a8e] rounded-lg shadow-lg p-6 text-white">
          <p className="text-sm font-medium opacity-90">Total Budget</p>
          <p className="text-3xl font-bold mt-2">{formatCurrency(totalBudget)}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <p className="text-sm font-medium text-gray-600">External Budget</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">
            {formatCurrency(allBudgets.find((b) => b.asJson.type === "External")?.asJson.totalAmount || 0)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <p className="text-sm font-medium text-gray-600">Internal Budget</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">
            {formatCurrency(allBudgets.find((b) => b.asJson.type === "Internal")?.asJson.totalAmount || 0)}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <h4 className="text-xl font-semibold text-gray-800 mb-6">{isEditing ? "Update Budget" : "Add Budget"}</h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Budget Type <span className="text-red-500">*</span>
            </label>
            <select
              className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#84cbe9] focus:border-[#84cbe9] transition-colors ${
                typeError ? "border-red-500" : "border-gray-300"
              }`}
              value={budget.type}
              onChange={(e) => {
                setBudget({ ...budget, type: e.target.value })
                setTypeError("")
              }}
            >
              <option value="" disabled>
                Select budget type
              </option>
              <option value="External">External</option>
              <option value="Internal">Internal</option>
            </select>
            {typeError && <p className="mt-2 text-sm text-red-600">{typeError}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Amount <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                className="w-full pl-8 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#84cbe9] focus:border-[#84cbe9] transition-colors"
                type="number"
                value={budget.totalAmount || ""}
                onChange={(e) =>
                  setBudget({
                    ...budget,
                    totalAmount: e.target.valueAsNumber,
                  })
                }
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>
          </div>
        </div>

        <div className="mt-6 flex gap-3">
          <button
            className="px-6 py-2.5 bg-[#84cbe9] text-white font-medium rounded-lg hover:bg-[#3f6a8e] transition-colors duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? "Saving..." : isEditing ? "Update Budget" : "Add Budget"}
          </button>
          {isEditing && (
            <button
              className="px-6 py-2.5 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors duration-200"
              onClick={() => {
                setBudget({ ...defaultTrainingBudget })
                setIsEditing(false)
                setTypeError("")
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h4 className="text-xl font-semibold text-gray-800 mb-6">Existing Budgets</h4>

        {allBudgets.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No budgets created yet. Add your first budget above.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {allBudgets.map((item) => (
              <div
                key={item.asJson.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-[#84cbe9] transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div
                    className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      item.asJson.type === "External" ? "bg-blue-100" : "bg-green-100"
                    }`}
                  >
                    <span className="text-2xl">{item.asJson.type === "External" ? "🌍" : "🏢"}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{item.asJson.type} Training</p>
                    <p className="text-2xl font-bold text-[#3f6a8e]">{formatCurrency(item.asJson.totalAmount)}</p>
                  </div>
                </div>
                <button
                  className="px-4 py-2 bg-[#e7a637] text-white font-medium rounded-lg hover:bg-[#d69520] transition-colors duration-200"
                  onClick={() => handleEdit(item.asJson.id)}
                >
                  Edit
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default observer(Budget)
